import { makeAutoObservable, runInAction, computed, reaction } from 'mobx';
import SelectionController from './SelectionController';
import ClientSidePaginationWithSelect from './ClientSidePaginationWithSelect';

class DynamicListCreateStore {
    searchValue = '';
    pageIndex = 0;
    pageSize = 10;
    recipientsShown = [];
    loadingUsers = [];

    constructor(listStore) {
        makeAutoObservable(this, {
            availableLists: computed,
            paginatedLists: computed,
            totalPages: computed,
            selectedListsCount: computed,
            selectedEmployeesCount: computed,
        });
        this.listStore = listStore;

        // Initialize selection controller and pagination
        this.selection = new SelectionController(
            [],
            this,
            (x) => x.id,
            0
        );

        this.clientSidePaginationWithSelect =
            new ClientSidePaginationWithSelect(
                [],
                this,
                (x) => x.id,
                'name',
                this.selection
            );

        // React to changes in lists and search value
        this.disposeReaction = reaction(
            () => {
                // Access lists to create dependency
                const lists = this.listStore.lists;
                return { 
                    listsLength: lists.length, 
                    searchValue: this.searchValue,
                    pageIndex: this.pageIndex
                };
            },
            () => {
                this.updatePagination();
            },
            { fireImmediately: true }
        );
    }

    setPagingParams(pageIndex, pageSize) {
        this.pageIndex = pageIndex;
        if (pageSize) {
            this.pageSize = pageSize;
        }
    }

    setSearchValue(value) {
        this.searchValue = value;
        this.pageIndex = 0;
    }

    get availableLists() {
        const allLists = this.listStore.lists || [];
        
        return allLists.filter((list) => {
            // Exclude lists with metadata.type === 'awarenessImprovements'
            if (list.metadata && list.metadata.type === 'awarenessImprovements') {
                return false;
            }
            // Filter by search value
            if (this.searchValue && list.name) {
                return list.name.toLowerCase().includes(this.searchValue.toLowerCase());
            }
            return true;
        });
    }

    updatePagination() {
        runInAction(() => {
            this.clientSidePaginationWithSelect.data = this.availableLists;
            this.selection.setData(
                this.clientSidePaginationWithSelect.pagedData,
                this.availableLists.length
            );
        });
    }

    get paginatedLists() {
        return this.clientSidePaginationWithSelect.pagedData;
    }

    get totalPages() {
        return this.clientSidePaginationWithSelect.pageCount;
    }

    toggleRecipients(listId) {
        if (this.recipientsShown.includes(listId)) {
            this.recipientsShown = this.recipientsShown.filter((x) => x !== listId);
        } else {
            this.recipientsShown.push(listId);
        }
    }

    async loadUsersForList(companyId, listId) {
        if (this.loadingUsers.includes(listId)) return;
        
        runInAction(() => {
            this.loadingUsers.push(listId);
        });
        
        try {
            const services = (await import('../services')).default;
            const listUsers = await services.Companies.listsService(companyId).users(listId);
            const list = this.availableLists.find((l) => l.id === listId);
            if (list) {
                runInAction(() => {
                    list.users = listUsers;
                });
            }
        } catch (e) {
            console.error('Error loading users for list:', e);
        } finally {
            runInAction(() => {
                this.loadingUsers = this.loadingUsers.filter((x) => x !== listId);
            });
        }
    }

    getSelectedListIds() {
        return this.clientSidePaginationWithSelect.selectedData.map((x) => x.id);
    }

    get selectedListsCount() {
        return this.getSelectedListIds().length;
    }

    get selectedEmployeesCount() {
        const selectedIds = this.getSelectedListIds();
        return this.availableLists
            .filter((list) => selectedIds.includes(list.id))
            .reduce((total, list) => total + (list.usersCount || 0), 0);
    }

    reset() {
        this.searchValue = '';
        this.pageIndex = 0;
        this.recipientsShown = [];
        this.selection.clearSelection();
    }

    // Cleanup reaction on destroy
    dispose() {
        if (this.disposeReaction) {
            this.disposeReaction();
        }
    }
}

export default DynamicListCreateStore;

